import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";

import landingPreviewHeaderBG from "images/landing-page/landing-preview-header-bg.png";

import { classnames, Icon, Loader } from "@culturehq/components";

import { getName, t, Trans } from "app/utils";

import LandingPageStories from "components/admin/LandingPageStories";
import { TimeDiffYears, getTimeDiff } from "components/common/Time";
import { ProgramLink, DegreeLink } from "app/links";
import Tags from "components/person/Tags";
import AccessibleFocusOutlineElement from "components/common/AccessibleFocusOutlineElement";
import { useWindowDimensions } from "app/hooks";
import getUserName from "app/utils/getUserName";

const LandingPageUserPreviewCallout = () => {
  const { userId } = useParams();
  const [showUserInfo, setShowUserInfo] = useState(true);

  const getUrl = (isBlob, localUrl) => {
    if (isBlob) {
      return localUrl;
    }

    return localUrl !== "undefined" ? JSON.parse(localUrl) : "";
  };

  const [orgName] = useState(JSON.parse(localStorage.getItem("orgName")));
  const [isHeaderImageBlob] = useState(JSON.parse(localStorage.getItem("isHeaderImageBlob")));
  const [storiesState] = useState(JSON.parse(localStorage.getItem("stories")));
  const [usersState] = useState(JSON.parse(localStorage.getItem("users")));
  const localHeaderImageUrl = localStorage.getItem("headerImageUrl");
  const [headerImageUrl] = useState(getUrl(isHeaderImageBlob, localHeaderImageUrl));
  const [primaryColor] = useState(JSON.parse(localStorage.getItem("primaryColor")));
  const [user] = useState(usersState.find(user => `${user.id}` === userId));
  const { _, width } = useWindowDimensions();
  const [stories] = useState(storiesState.filter(story => `${story.creator.id}` === userId));

  const getSelectedHeaderImage = () => {
    if (typeof headerImageUrl === "string") {
      return headerImageUrl;
    }

    return URL.createObjectURL(headerImageUrl);
  };

  if (!usersState && !storiesState) {
    return <Loader loading />;
  }

  return (
    <div className={classnames("LandingPageCallout", "LandingPageUserCallout", orgName)}>
      <div className="LandingPageCallout--Preview">
        <div className="Preview">
          <div className="Preview-Header__Image" style={{ backgroundImage: headerImageUrl ? `url(${getSelectedHeaderImage()}` : `url(${landingPreviewHeaderBG})` }}>
            <div className="Preview-Header__Overlay">
              <h1 className="Preview-Header__Text" style={{ backgroundImage: `linear-gradient(120deg, ${primaryColor} 0%, ${primaryColor}38 100%)` }}>
                <Trans>Meet {getName(user)}</Trans>
              </h1>
            </div>
          </div>
          <div className="Preview-Body row">
            <div className="col-md-3 Preview-LeftColumn view-sec">
              <Link className="back-arrow" to="/admin/landing-page/preview">
                <Icon icon="arrow-left-c" />
                <Trans>Go Back</Trans>
              </Link>
              <img className="Preview-Logo" src={user.avatar.mediumUrl} alt={t("User photo")} />
              <h1 className="Person--name">{getUserName(user)}</h1>
              <h2 className="Person--title">{user.title}</h2>
              <ul className="e_label show-md">
                <li className={showUserInfo ? "selected" : ""} onClick={() => setShowUserInfo(true)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Trans>About</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
                <li className={!showUserInfo ? "selected" : ""} onClick={() => setShowUserInfo(false)}>
                  <AccessibleFocusOutlineElement tag="button" className="chq-btn">
                    <Trans>Stories</Trans>
                  </AccessibleFocusOutlineElement>
                </li>
              </ul>
              {(showUserInfo || width > 992) && (
                <div className="Preview-Tags">
                  <div className="Section__title">
                    <h2 className="subtitle" style={{ color: primaryColor }}><Trans>HIGHLIGHTS</Trans></h2>
                  </div>
                  <div className="Section__highlights">
                    {user.startedOn && (<p aria-label={t(`Time in the company: ${getTimeDiff(user.startedOn)}`)}><Icon icon="clock" /><TimeDiffYears time={user.startedOn} /></p>)}
                    {user.location && (<p aria-label={t(`Location:  ${user.location.name}`)}><Icon icon="loc-ol" /><span>{user.location.name}</span></p>)}
                    {user.departments.length > 0 && (
                      <p aria-label={t("Departments:")}>
                        <Icon icon="ios-people-outline" />
                        <span>
                          {user.departments.map((department, index) => (
                            <span key={department.id}>
                              <span>{department.name}</span>
                              {index !== user.departments.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                    {user.companies.length > 0 && (
                      <p aria-label={t("Companies:")}>
                        <Icon icon="building" />
                        <span>
                          {user.companies.map((company, index) => (
                            <span key={company.id}>
                              <span>{company.name}</span>
                              {index !== user.companies.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                    {user.degree && (<p aria-label={t(`Degree:  ${user.degree}`)}><Icon icon="home-outlined" /><DegreeLink degree={user.degree} /></p>)}
                    {user.programs?.length > 0 && (
                      <p aria-label={t("Programs:")}>
                        <Icon icon="ios-star-outline" />
                        <span>
                          {user.programs.map((program, index) => (
                            <span key={program.id}>
                              <ProgramLink program={program} />
                              {index !== user.programs.length - 1 && ", "}
                            </span>
                          ))}
                        </span>
                      </p>
                    )}
                  </div>

                  {user.interests.length !== 0 && (
                    <Tags
                      color={primaryColor}
                      icon="heart-ol"
                      iconCommonality="heart"
                      label="INTERESTS"
                      myTags={[]}
                      userTags={user.interests}
                      landingPage
                    />
                  )}
                  {user.skills.length !== 0 && (
                    <Tags
                      color={primaryColor}
                      icon="hammer"
                      iconCommonality="hammer-filled"
                      label="SKILLS"
                      myTags={[]}
                      userTags={user.skills}
                      landingPage
                    />
                  )}
                </div>
              )}
            </div>
            {(!showUserInfo || width > 992) && (
              <div className="col-md-9">
                <div className="Preview-Browser__Stories">
                  <h2><Trans>{getName(user)}&apos;s stories</Trans></h2>
                  <LandingPageStories
                    stories={stories}
                    hideCreator
                    orgName={orgName}
                    preview
                    users={[user]}
                    userProfile
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageUserPreviewCallout;
